import React, { useState, useEffect, useCallback } from 'react';
import { apiGET } from '../utilities/apiHelpers';
import { toast } from 'react-toastify';
import { FiEye, FiTruck, FiCheckCircle, FiClock, FiX, FiPackage, FiMaximize2 } from "react-icons/fi";
import moment from "moment";
import { getImageWithFallback } from "../utilities/getImageWithFallback";
import Pagination from "../components/Pagination/Pagination";

const RefundTrackingPage = () => {
  const [loading, setLoading] = useState(true);
  const [trackingLoading, setTrackingLoading] = useState(false);
  const [refunds, setRefunds] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [hoveredCard, setHoveredCard] = useState(null);
  const [selectedRefund, setSelectedRefund] = useState(null);
  const [liveTracking, setLiveTracking] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);

  const getUserIdFromToken = () => {
    try {
      const token = localStorage.getItem('accessToken');
      if (!token) return null;
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const decoded = JSON.parse(window.atob(base64));
      return decoded.id || decoded._id || decoded.sub;
    } catch (e) { return null; }
  };

  const userId = getUserIdFromToken();

  const fetchRefundRequests = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const res = await apiGET(`/v1/return-request/${userId}?page=${page}&limit=8`);
      if (res?.data?.status) {
        setRefunds(res.data.data.data || []);
        setTotalPages(res.data.data.totalPages || 0);
      }
    } catch (err) {
      toast.error('Failed to load tracking list');
    } finally {
      setLoading(false);
    }
  }, [userId, page]);

  const fetchLiveTracking = async (refundId) => {
    if (!refundId) return;
    setTrackingLoading(true);
    try {
      const res = await apiGET(`/v1/payment/refund/${refundId}`);
      if (res?.data?.success) {
        setLiveTracking(res.data.data);
      }
    } catch (err) {
      console.error("Refund Tracking Error:", err);
      const errorMessage = err?.response?.data?.msg || err?.response?.data?.message || "Tracking info unavailable";
      toast.error(errorMessage);
    } finally {
      setTrackingLoading(false);
    }
  };

  useEffect(() => {
    fetchRefundRequests();
  }, [fetchRefundRequests]);

  const handleOpenModal = (refund) => {
    setSelectedRefund(refund);
    setLiveTracking(null);
    const rzpId = refund.payment_info?.[0]?.refundId || refund.refundId;
    if (rzpId) {
      fetchLiveTracking(rzpId);
    }
  };

  return (
    <div className="container mx-auto p-4 min-h-screen font-sans bg-[#f8fafc]">

      {/* LIGHTBOX PREVIEW */}
      {previewImage && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/90 backdrop-blur-md p-4 cursor-zoom-out animate-fadeIn"
          onClick={() => setPreviewImage(null)}
        >
          <button className="absolute top-6 right-6 text-white hover:text-red-500 transition-colors">
            <FiX size={32} />
          </button>
          <img
            src={previewImage}
            alt="Full Preview"
            className="max-w-full max-h-full rounded-lg shadow-2xl animate-scaleIn object-contain"
          />
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-20">
          <div className="w-10 h-10 border-4 border-[#14967F] border-t-transparent rounded-full animate-spin"></div>
        </div>
      ) : refunds.length > 0 ? (
        <>
          <div className="font-medium text-2xl mb-6 text-gray-800">Refund Tracking</div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            {refunds.map((request) => {
              const firstItem = request.orderItems?.[0];
              const productImg = getImageWithFallback([firstItem?.productId?.bannerImg], "product");
              const isHovered = hoveredCard === request._id;
              const itemCount = request.orderItems?.length || 0;

              return (
                <div
                  key={request._id}
                  onMouseEnter={() => setHoveredCard(request._id)}
                  onMouseLeave={() => setHoveredCard(null)}
                  onClick={() => handleOpenModal(request)}
                  className="bg-white rounded-xl border border-gray-200 flex h-32 overflow-hidden transition-all duration-300 cursor-pointer group shadow-sm"
                  style={{
                    boxShadow: isHovered ? '0 10px 15px -3px rgba(0, 0, 0, 0.07)' : '0 1px 2px rgba(0,0,0,0.03)',
                    transform: isHovered ? 'translateY(-2px)' : 'none'
                  }}
                >
                  {/* Image Section - Main image full visibility */}
                  <div className="w-1/3 h-full bg-gray-50 flex items-center justify-center p-2 relative border-r border-gray-100 overflow-hidden">
                    <img
                      src={productImg}
                      alt="product"
                      className="w-full h-full object-contain mix-blend-multiply transition-transform duration-500 group-hover:scale-110"
                    />
                    {itemCount > 1 && (
                      <div className="absolute bottom-2 left-2 bg-gray-800/80 text-white text-[10px] px-2 py-0.5 rounded-full backdrop-blur-sm font-bold shadow-sm">
                        +{itemCount - 1} items
                      </div>
                    )}
                  </div>

                  <div className="w-2/3 p-3 flex flex-col justify-between bg-white">
                    <div className="flex flex-col gap-1">
                      <div
                        title="Track Refund"
                        className="self-end flex items-center gap-1 px-2 py-2 bg-[#14967F] text-white rounded text-[10px] font-bold cursor-pointer transition-opacity hover:opacity-90"
                      >
                        <FiEye size={12} />
                      </div>
                      <h3 className="text-[11px] font-bold text-gray-800 truncate" title={firstItem?.productId?.name}>
                        {firstItem?.productId?.name || "Order Items"}
                      </h3>
                      <p className="text-[9px] font-mono text-gray-400">
                        ID: {request._id?.slice(-6).toUpperCase()}
                      </p>
                    </div>
                    <div className="flex justify-between items-end border-t border-gray-100 pt-2">
                      <div className="flex flex-col">
                        {/* Show the original price and the charges deduction text */}
                        {((request.packaging_charges || 0) + (request.delivery_charges || 0)) > 0 && (
                          <div className="flex items-center gap-1 text-[9px] font-bold">
                            <span className="text-gray-400 line-through">₹{request.totalPayment}</span>
                            <span className="text-red-500">- ₹{(request.packaging_charges || 0) + (request.delivery_charges || 0)} Charges</span>
                          </div>
                        )}
                        <span className="text-sm font-bold text-[#14967F]">
                          ₹{request.totalPayment - ((request.packaging_charges || 0) + (request.delivery_charges || 0))}
                        </span>
                      </div>
                      <span className="text-[9px] text-gray-400 font-medium mb-0.5">
                        {moment(request.createdAt).format("DD MMM")}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          <div className="flex justify-center">
            <Pagination currentPage={page} totalPages={totalPages} onPageChange={setPage} />
          </div>
        </>
      ) : (
        <div className="flex flex-col items-center justify-center py-20 bg-white rounded-3xl border border-dashed border-gray-200">
          <h3 className="text-lg font-bold text-gray-800">No Refund History</h3>
          <button onClick={() => window.location.href = '/orders'} className="mt-6 px-6 py-2 bg-[#14967F] text-white rounded-xl">View Recent Orders</button>
        </div>
      )}

      {/* TRACKING MODAL */}
      {selectedRefund && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-fadeIn">
          <div className="bg-white rounded-[2rem] shadow-2xl w-full max-w-xl overflow-hidden flex flex-col border border-gray-300 animate-scaleIn">

            {/* Modal Header */}
            <div className="p-6 pb-4 flex justify-between items-start bg-slate-50/80 border-b border-gray-100">
              <div>
                <h3 className="font-bold text-2xl text-gray-900">Refund Details</h3>
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-[10px] bg-white border border-gray-200 px-2 py-0.5 rounded font-bold text-gray-500 uppercase">
                    REF: {selectedRefund._id?.slice(-8).toUpperCase()}
                  </span>
                  <span className="text-[10px] text-gray-400 font-medium ">Requested {moment(selectedRefund.createdAt).fromNow()}</span>
                </div>
              </div>
              <button onClick={() => setSelectedRefund(null)} className="w-10 h-10 flex items-center justify-center bg-white text-gray-400 rounded-xl hover:text-red-500 transition-all shadow-sm border border-gray-100">
                <FiX size={20} />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6 space-y-6 overflow-y-auto max-h-[75vh]">

              {/* SECTION: ORDER ITEMS GRID (Multi-item support) */}
              <div>
                <div className="flex items-center gap-2 mb-3 text-gray-800">
                  <FiPackage className="text-[#14967F]" />
                  <span className="text-xs font-bold uppercase tracking-wider">Items in this Refund</span>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {selectedRefund.orderItems?.map((item, index) => {
                    const imgUrl = getImageWithFallback([item.productId?.bannerImg], "product");
                    return (
                      <div key={item._id || index} className="flex items-center gap-3 p-3 rounded-xl border border-gray-100 bg-gray-50/50 group">
                        <div
                          className="relative w-12 h-12 flex-shrink-0 bg-white rounded-lg shadow-sm overflow-hidden border border-gray-100 cursor-zoom-in"
                          onClick={() => setPreviewImage(imgUrl)}
                        >
                          <img src={imgUrl} alt="product" className="w-full h-full object-contain mix-blend-multiply group-hover:scale-110 transition-transform duration-300" />
                          <div className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity">
                            <FiMaximize2 className="text-white" size={14} />
                          </div>
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="font-bold text-gray-900 text-[11px] truncate">{item.productId?.name || "Medicine Item"}</h4>
                          <p className="text-[10px] text-gray-500 mt-0.5">Qty: {item.quantity || 1}<span className="font-bold text-gray-700"></span></p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* SECTION: PAYMENT SUMMARY */}
              <div className="p-4 rounded-2xl bg-[#14967F]/5 border border-[#14967F]/10">
                <div className="flex justify-between items-center mb-4">
                  <div>
                    <span className="text-[10px] uppercase tracking-wider text-gray-500 font-bold block">
                      Total Refund Amount
                    </span>
                    <p className="text-2xl font-black text-[#14967F]">
                      ₹{selectedRefund.totalPayment - ((selectedRefund.packaging_charges || 0) + (selectedRefund.delivery_charges || 0))}
                    </p>

                    {/* Explicit Charges Text */}
                    {((selectedRefund.packaging_charges || 0) + (selectedRefund.delivery_charges || 0)) > 0 && (
                      <div className="flex flex-col mt-1 ">
                        <span className="text-[10px] text-gray-400 line-through">
                          Original: ₹{selectedRefund.totalPayment}
                        </span>
                        <span className="text-[10px] text-red-500 font-bold mt-1">
                          - ₹{(selectedRefund.packaging_charges || 0) + (selectedRefund.delivery_charges || 0)} Charges
                        </span>
                      </div>
                    )}
                  </div>
                  <div className="bg-white px-3 py-1.5 rounded-lg border border-[#14967F]/20 text-center">
                    <p className="text-[10px] font-bold text-gray-400 uppercase leading-none">Status</p>
                    <p className="text-[11px] font-black text-[#14967F] uppercase mt-1">{liveTracking?.status || "Processing"}</p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4 pt-3 border-t border-[#14967F]/10">
                  <div>
                    <span className="text-[9px] uppercase tracking-wider text-gray-400 font-bold block">Refund ID</span>
                    <p className="text-[11px] font-mono text-gray-700 truncate">{liveTracking?.refundId || "Fetching..."}</p>
                  </div>
                  <div>
                    <span className="text-[9px] uppercase tracking-wider text-gray-400 font-bold block">Payment ID</span>
                    <p className="text-[11px] font-mono text-gray-700 truncate">{liveTracking?.paymentId || "Not Available"}</p>
                  </div>
                </div>
              </div>

              {/* TIMELINE */}
              <div className="relative pl-8 space-y-6 pb-2">
                <div className="absolute left-[11px] top-2 bottom-2 w-0.5 bg-gray-100"></div>
                <div className="relative">
                  <div className="absolute -left-8 w-6 h-6 rounded-full bg-green-500 flex items-center justify-center text-white z-10 shadow-sm">
                    <FiCheckCircle size={14} />
                  </div>
                  <h5 className="text-sm font-bold text-gray-800">Refund Requested</h5>
                  <p className="text-[11px] text-gray-500">{moment(selectedRefund.createdAt).format("DD MMM YYYY, hh:mm A")}</p>
                </div>
                <div className="relative">
                  <div className={`absolute -left-8 w-6 h-6 rounded-full flex items-center justify-center text-white z-10 shadow-sm ${liveTracking?.status === 'processed' ? 'bg-green-500' : 'bg-orange-400 animate-pulse'}`}>
                    {liveTracking?.status === 'processed' ? <FiCheckCircle size={14} /> : <FiClock size={14} />}
                  </div>
                  <div className="flex flex-col">
                    <h5 className="text-sm font-bold text-gray-800">{trackingLoading ? "Fetching status..." : (liveTracking?.status ? `Bank Processing: ${liveTracking.status.toUpperCase()}` : "Processing Refund")}</h5>
                    <div className="mt-2 p-3 bg-slate-50 rounded-xl border border-dashed border-gray-200">
                      <p className="text-[10px] text-gray-500 ">Expected to reflect in 5-7 business days once processed.</p>
                    </div>
                  </div>
                </div>
                <div className={`relative ${liveTracking?.status !== 'processed' && 'opacity-40'}`}>
                  <div className={`absolute -left-8 w-6 h-6 rounded-full flex items-center justify-center text-white z-10 shadow-sm ${liveTracking?.status === 'processed' ? 'bg-gray-400' : 'bg-gray-200'}`}>
                    <FiTruck size={12} />
                  </div>
                  <h5 className="text-sm font-bold text-gray-700">Funds in Bank Account</h5>
                  <p className="text-[11px] text-gray-400">Estimated Credit: {moment(selectedRefund.createdAt).add(7, 'days').format("DD MMM YYYY")}</p>
                </div>
              </div>

            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RefundTrackingPage;